import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useNewStore, usePlayerContext, useStyles } from '../../../../../common/hooks';
import { getVolumeIcon } from '../../../../../common/utils/tools';
import { Button, Slider } from '../../../common';
import { VOLUME_HIDDEN_STYLES, VOLUME_SLIDER_BUTTON_SEPARATOR, VOLUME_SLIDER_DEFAULT_DIALOG_STYLE, VOLUME_SLIDER_STYLE, VOLUME_VISIBLE_STYLES } from './styles';

import { TVShortcutController } from '../../../../../../core/shortcut/tvshortcut';
import { BACK_BTN_SHORTCUTS } from '../../../../../../core/shortcut/types';
import { UI_IS_INTERACTING } from '../../../../../../store/types';
import { USER_CLICK, USER_MUTE, USER_VOLUME_CHANGE } from '../../../../../../types';
import { SimpleTooltip } from '../../../../../common/components/tooltips/SimpleTooltip';
import { BUTTON_LAYER, MAX_LAYER } from '../../../../../common/constants';
import { useDialogContext, useRegisterDialog } from '../../../../../common/context/DialogContext';
import { VOLUME_DIALOG_LAYER } from '../../../../theme/constants';

function Volume({
  volume,
  muted,
  medium,
  extraSmall,
  small,
  name,
  disableFocus,
  size: buttonSize,
  ...extraProps
}) {
  const resolveStateToSliderVal = (vol) => Math.ceil((100 * vol)).toFixed(2);

  const player = usePlayerContext();
  const dialogCtx = useDialogContext();
  const activeIconRef = useRef(null);
  const { store } = usePlayerContext();
  const [volumeState, setVolumeState] = useState(volume);
  const [sliderVal, setSliderVal] = useState(resolveStateToSliderVal(volumeState));
  const [sliding, setSliding] = useState(false);
  const [opened, setOpened] = useState(false);
  const setStyles = useStyles();
  const { currentBreakpoint } = useNewStore('ui', player);

  useRegisterDialog({
    id: name, openingMode: 'over', state: [opened, setOpened]
  });

  useEffect(() => !sliding && setVolumeState(volume), [volume]);

  useEffect(() => {
    setSliderVal(resolveStateToSliderVal(volumeState));

    if (volumeState !== volume) player.volume(volumeState);
  }, [volumeState]);

  useEffect(() => (sliding && muted && player.mute(false)), [sliding, muted]);

  const toggleMute = () => {
    player.userEvents$.next({ action: USER_MUTE, value: !muted, source: USER_CLICK });
    player.mute(!muted);
  };

  const sliderStyle = setStyles(VOLUME_SLIDER_STYLE);

  const handleSlide = (pos, maxPos) => {
    const val = pos / maxPos;

    setVolumeState(parseFloat(val.toFixed(2)));
  };

  const handleStopSlide = (pos, maxPos) => {
    player.userEvents$.next({ action: USER_VOLUME_CHANGE, source: USER_CLICK, value: parseFloat((pos / maxPos).toFixed(2)) });
    setSliding(false);
  };

  const [sliderDynamicStyles, setSliderDynamicStyles] = useState(VOLUME_HIDDEN_STYLES);
  const dialogContainerRef = useRef(null);

  useEffect(() => {
    store.dispatch({ type: UI_IS_INTERACTING, payload: { isInteracting: opened, volumeSliderVisible: opened } });
    setSliderDynamicStyles(opened ? VOLUME_VISIBLE_STYLES : VOLUME_HIDDEN_STYLES);
  }, [opened]);

  const onFocusSlider = (e) => {
    if (dialogContainerRef.current && dialogContainerRef.current.contains(e.target)) {
      setOpened(true);
    }
  };

  const onBlurContainer = () => {
    setTimeout(() => {
      if (dialogContainerRef.current && !dialogContainerRef.current.contains(document.activeElement)) {
        setOpened(false);
      }
    }, 0);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="ftv-magneto--glass-wrapper"
      ref={dialogContainerRef}
      style={{ position: 'relative', display: 'flex' }}
      onBlur={onBlurContainer}
      onMouseEnter={() => {
        setOpened(true);
      }}
      onMouseLeave={() => {
        setOpened(false);
      }}
      onKeyDown={(e) => {
        if (dialogCtx.dialogOpened && TVShortcutController.getPrioritizedKeyCode(e, [BACK_BTN_SHORTCUTS])) {
          e.preventDefault();
          dialogCtx.closeAll();
          activeIconRef?.current?.focus();
        }
      }}
    >
      <Button
        ref={activeIconRef}
        type="icon"
        label={!opened ? 'Son (m)' : ''}
        forceLabel
        styles={{
          ...(!extraSmall && opened && {
            subWrapperStyle: {
              borderRadius: '30px'
            }
          })
        }}
        onClick={toggleMute}
        icon={getVolumeIcon({ volume, muted })}
        openingMode="over"
        xs
        name={name}
        disableFocus={disableFocus}
        zIndex={BUTTON_LAYER + 1}
        size={buttonSize}
        hideLabel={opened}
        {...extraProps}
      />
      <div
        className="ftv-magneto--glass ftv-magneto--blur-dense ftv-magneto--glass--bordered"
        style={{
          ...VOLUME_SLIDER_DEFAULT_DIALOG_STYLE,
          ...sliderDynamicStyles,
          zIndex: ['xs', 'sm'].includes(currentBreakpoint) ? 'auto' : MAX_LAYER
        }}
      >
        <SimpleTooltip
          content="Son (m)"
          position="top"
          forceVisible={opened}
          enableBackground={false}
        >
          <div style={VOLUME_SLIDER_BUTTON_SEPARATOR} />

          <Slider
            value={muted ? 0 : sliderVal}
            strokeSize={6}
            cursorSize={sliderStyle.cursorSize}
            trackSize={sliderStyle.trackSize}
            onFocus={onFocusSlider}
            onMouseEnter={onFocusSlider}
            onBlur={() => setOpened(false)}
            onSlide={handleSlide}
            onStartSlide={() => setSliding(true)}
            onStopSlide={handleStopSlide}
            styles={{
              container: {
                padding: `${sliderStyle.verticalPadding}px ${sliderStyle.horizontalPadding}px `,
                zIndex: VOLUME_DIALOG_LAYER,
                width: '100%'
              },
              outerTrack: {
                width: '100%'
              }
            }}
            vertical
            name="slider-volume"
            accessibleName="volume"
            disableFocus={disableFocus}
            showSlider={!extraSmall}
          />
        </SimpleTooltip>
      </div>
    </div>
  );
}

export default Volume;
